import { useState, useEffect, useContext } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  List,
  ListItem,
  Grid,
  Menu,
  MenuItem,
  Button,
  ButtonGroup,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ReassignDialog from "./ReassignDialog";
import request from "../../../utilities/requests";
import { EventContext } from "../../../contexts/EventContext";

const GroupByPerson = ({ data }) => {
  const [pairings, setPairings] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSwapOutRound, setSelectedSwapOutRound] = useState(null);
  const [showSwapSelect, setShowSwapSelect] = useState(false);
  const [selectedSwapIn, setSelectedSwapIn] = useState(null);
  const { pairingEvent, setPairingData, pairingData } =
    useContext(EventContext);
  const [expanded, setExpanded] = useState(false);
  const [showRounds, setShowRounds] = useState(true);

  useEffect(() => {
    setPairings(data);
  }, [data]);

  useEffect(() => {
    if (
      pairingEvent &&
      ["mixer", "dinner"].includes(pairingEvent?.pairingEventType)
    ) {
      setShowRounds(false);
    }
  }, [pairingEvent]);

  const handleMenuOpen = (event, attendee, round) => {
    setAnchor(event.currentTarget);
    setMenuOpen(true);
    setSelectedSwapOutRound(round);
  };

  const handleSwapOutSelect = () => {
    setMenuOpen(false);
    setShowSwapSelect(true);
  };

  const handleSwapInSelect = (attendee) => {
    setSelectedSwapIn(attendee._id);
    setDialogOpen(true);
  };

  const handleCancel = () => {
    setDialogOpen(false);
    setShowSwapSelect(false);
    setSelectedSwapIn(null);
    setSelectedSwapOutRound(null);
  };

  const handleLike = async (pairingId, userId, val) => {
    const body = {
      pairingId,
      userId,
      like: val,
    };
    const result = await request("pairing/like", "post", body);
    if (result.success) {
      getPairingData();
    }
  };

  const getPairingData = async () => {
    const result = await request(
      `pairingevent/pairings?pairingEventId=${pairingEvent._id}`
    );
    if (result.success) {
      setPairingData(result.data);
    }
  };

  const renderLike = (attendee, round) => {
    if (round.userLike === true) {
      return (
        <FavoriteIcon
          onClick={() => handleLike(round.pairingId, attendee._id, false)}
          style={{ cursor: "pointer" }}
        />
      );
    }

    return (
      <FavoriteBorderIcon
        onClick={() => handleLike(round.pairingId, attendee._id, true)}
        style={{ cursor: "pointer" }}
      />
    );
  };

  const handleSwap = async () => {
    const body = {
      pairingId: selectedSwapOutRound.pairingId, // pairing ID of the pairing that will be updated
      swappedUserId: selectedSwapOutRound.match._id, // the user that is going to be swapped to another person
      swapInUserId: selectedSwapIn, // user id of person being added to the pairing
    };

    const result = await request("pairing/reassign", "post", body);
    if (result.success) {
      setPairingData(result.data);
    }
    handleCancel();
  };

  const handleAccordionChange = (attendeeEmail) => (event, isExpanded) => {
    setExpanded(isExpanded ? attendeeEmail : false);
  };

  const renderAttendeeHeader = (count, d) => {
    const lastName = d.attendee?.profile?.lastName
      ? d.attendee?.profile?.lastName[0]
      : "";
    let val = `${count} | ${d.attendee?.profile?.firstName} ${lastName}`;
    if (showRounds) {
      const rnds =
        pairingEvent?.pairingEventType === "group"
          ? d.roundCount
          : d.rounds.length;
      val = val + ` | Rounds: ${rnds}`;
    }
    return val;
  };

  const renderBreak = (attendee) => {
    try {
      if (pairingEvent.event.status !== "active") return null;
      const hasRound = attendee.rounds.find(
        (r) => r.round === pairingEvent?.currentRound
      );
      if (!hasRound) {
        return (
          <Typography align="right" sx={{ color: "red", marginLeft: "20px" }}>
            Break
          </Typography>
        );
      }
      return null;
    } catch (e) {
      return null;
    }
  };

  let count = 0;
  return (
    <>
      {pairings.map((d, i) => {
        count++;
        return (
          <Accordion
            key={d.attendee.email}
            expanded={expanded === d.attendee.email}
            onChange={handleAccordionChange(d.attendee.email)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`panel-${d.attendee.email}-content`}
              id={`panel-${d.attendee.email}-header`}
            >
              <Typography variant="body1">
                {renderAttendeeHeader(count, d)}
              </Typography>
              {renderBreak(d)}
            </AccordionSummary>
            {expanded === d.attendee.email && (
              <AccordionDetails>
                {showSwapSelect ? (
                  <ButtonGroup>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleSwapInSelect(d.attendee)}
                    >
                      Select
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleCancel}
                    >
                      Cancel
                    </Button>
                  </ButtonGroup>
                ) : null}
                <List>
                  <ListItem>
                    <Grid container direction="row">
                      {showRounds && (
                        <Grid item xs={2} sm={2} md={2}>
                          <Typography
                            align="center"
                            variant="subtitle1"
                            sx={{
                              fontWeight: "bold",
                              fontSize: {
                                xs: "0.75rem", // Extra-small devices
                                sm: "0.875rem", // Small devices
                                md: "1rem", // Medium devices and up
                              },
                            }}
                          >
                            Round
                          </Typography>
                        </Grid>
                      )}
                      {showRounds && (
                        <Grid item xs={2} sm={2} md={2}>
                          <Typography
                            align="center"
                            variant="subtitle1"
                            sx={{
                              fontWeight: "bold",
                              fontSize: {
                                xs: "0.75rem",
                                sm: "0.875rem",
                                md: "1rem",
                              },
                            }}
                          >
                            Table
                          </Typography>
                        </Grid>
                      )}

                      <Grid item xs={3} sm={3} md={3}>
                        <Typography
                          align="center"
                          variant="subtitle1"
                          sx={{
                            fontWeight: "bold",
                            fontSize: {
                              xs: "0.75rem",
                              sm: "0.875rem",
                              md: "1rem",
                            },
                          }}
                        >
                          Match
                        </Typography>
                      </Grid>
                      <Grid item xs={2} sm={2} md={2}>
                        <Typography
                          align="center"
                          variant="subtitle1"
                          sx={{
                            fontWeight: "bold",
                            fontSize: {
                              xs: "0.75rem",
                              sm: "0.875rem",
                              md: "1rem",
                            },
                          }}
                        >
                          Score
                        </Typography>
                      </Grid>
                    </Grid>
                  </ListItem>
                  {d.rounds.map((r, j) => (
                    <ListItem key={`${i}-${j}-${r.match.profile._id}`}>
                      <Grid container direction="row">
                        {showRounds && (
                          <Grid item xs={2} sm={2} md={2}>
                            <Typography
                              align="center"
                              variant="subtitle1"
                              sx={{
                                fontSize: {
                                  xs: "0.75rem",
                                  sm: "0.875rem",
                                  md: "1rem",
                                },
                              }}
                            >
                              {r.round}
                            </Typography>
                          </Grid>
                        )}
                        {showRounds && (
                          <Grid item xs={2} sm={2} md={2}>
                            <Typography
                              align="center"
                              variant="subtitle1"
                              sx={{
                                fontSize: {
                                  xs: "0.75rem",
                                  sm: "0.875rem",
                                  md: "1rem",
                                },
                              }}
                            >
                              {r.table}
                            </Typography>
                          </Grid>
                        )}

                        <Grid item xs={3} sm={3} md={3}>
                          <Typography
                            align="center"
                            variant="subtitle1"
                            sx={{
                              fontSize: {
                                xs: "0.75rem",
                                sm: "0.875rem",
                                md: "1rem",
                              },
                            }}
                          >
                            {r.match.profile.firstName}{" "}
                          </Typography>
                        </Grid>
                        <Grid item xs={2} sm={2} md={2}>
                          <Typography
                            align="center"
                            variant="subtitle1"
                            sx={{
                              fontSize: {
                                xs: "0.75rem",
                                sm: "0.875rem",
                                md: "1rem",
                              },
                            }}
                          >
                            {r.matchScore}%
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={2}
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        >
                          {renderLike(d.attendee, r)}
                        </Grid>
                      </Grid>
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            )}
          </Accordion>
        );
      })}
      <Menu
        anchorEl={anchor}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
      >
        <MenuItem onClick={handleSwapOutSelect}>Reassign Match</MenuItem>
      </Menu>
      <ReassignDialog
        open={dialogOpen}
        close={handleCancel}
        confirm={handleSwap}
      />
    </>
  );
};

export default GroupByPerson;
